import { useCallback, useEffect, useState } from 'react';
import { TbLock } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { isValidPhone } from '../lib/phone';
import { memberApi as defaultMemberApi } from './member-api';
import './member.css';

const DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const ROLE_LABELS = { admin: 'Recepción', user: 'Alumno', coach: 'Profesor' };

function toFormState(data) {
  const profile = data && data.profile;
  const detailUser = data && data.user;
  return {
    username: (detailUser && detailUser.username) || '',
    fitnessGoal: (profile && profile.fitnessGoal) || '',
    targetDaysPerWeek: profile && profile.targetDaysPerWeek != null ? String(profile.targetDaysPerWeek) : '',
    phone: (profile && profile.phone) || '',
  };
}

/**
 * `/mi-cuenta/perfil` (spec "Alumno"): edits ONLY the `PATCH /profile`
 * whitelist — `username`, `fitnessGoal`, `targetDaysPerWeek`. Email, role and
 * account status come back in the same `GET /members/:id` detail and are
 * shown for context, but never as an editable control: the server itself
 * would silently ignore those keys from a member's own request, and the UI
 * never even offers them.
 */
export function MemberProfilePage({ api = defaultMemberApi }) {
  const { user, handleApiError, refreshSession } = useAuth();
  const [status, setStatus] = useState('loading');
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    username: '', fitnessGoal: '', targetDaysPerWeek: '', phone: '',
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setStatus('loading');
    setError('');
    try {
      const data = await api.getOwnMember(user.id);
      setDetail(data);
      setForm(toFormState(data));
      setStatus('loaded');
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData: () => setDetail(null) });
      if (outcome.handled) return;
      setStatus('error');
      setError(errorMessageFor(caught));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user && user.id, handleApiError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setNotice('');
    const trimmedPhone = form.phone.trim();
    if (!isValidPhone(trimmedPhone)) {
      setError('Ingresá un teléfono válido (dígitos, espacios, +, -, ( y )).');
      return;
    }

    setSubmitting(true);
    const fitnessGoal = form.fitnessGoal.trim() === '' ? null : form.fitnessGoal.trim();
    const targetDaysPerWeek = form.targetDaysPerWeek === '' ? null : Number(form.targetDaysPerWeek);
    const phone = trimmedPhone === '' ? null : trimmedPhone;
    try {
      await api.updateOwnProfile({
        username: form.username, fitnessGoal, targetDaysPerWeek, phone,
      });
      // Refresh the session (sidebar/name follow a username change) and
      // re-request the detail rather than trusting only the PATCH response,
      // so the form always reflects what the server actually persisted.
      await refreshSession();
      await load();
      setNotice('Guardamos los cambios.');
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData: () => setDetail(null) });
      if (!outcome.handled) setError(errorMessageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'loading') {
    return <PageState title="Mi perfil" status="loading" />;
  }

  if (status === 'error') {
    return (
      <PageState
        title="Mi perfil"
        status="error"
        message={error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  const detailUser = detail && detail.user;

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">Mi perfil</h1>
      </header>

      <form className="gym-form gym-profile-form" onSubmit={handleSubmit} noValidate>
        {error && <p role="alert" className="gym-form__error">{error}</p>}
        {notice && <p role="status" className="gym-form__notice">{notice}</p>}

        <div className="gym-field">
          <label htmlFor="profile-username" className="gym-field__label">Nombre</label>
          <input
            id="profile-username"
            name="username"
            type="text"
            required
            value={form.username}
            disabled={submitting}
            onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
            className="gym-input"
          />
        </div>

        <div className="gym-field">
          <label htmlFor="profile-goal" className="gym-field__label">Objetivo</label>
          <input
            id="profile-goal"
            name="fitnessGoal"
            type="text"
            value={form.fitnessGoal}
            disabled={submitting}
            onChange={(event) => setForm((prev) => ({ ...prev, fitnessGoal: event.target.value }))}
            className="gym-input"
          />
        </div>

        <div className="gym-field">
          <label htmlFor="profile-days" className="gym-field__label">Días por semana</label>
          <select
            id="profile-days"
            name="targetDaysPerWeek"
            value={form.targetDaysPerWeek}
            disabled={submitting}
            onChange={(event) => setForm((prev) => ({ ...prev, targetDaysPerWeek: event.target.value }))}
            className="gym-input"
          >
            <option value="">Sin definir</option>
            {DAYS_OPTIONS.map((day) => <option key={day} value={day}>{day}</option>)}
          </select>
        </div>

        <div className="gym-field">
          <label htmlFor="profile-phone" className="gym-field__label">Teléfono</label>
          <input
            id="profile-phone"
            name="phone"
            type="tel"
            value={form.phone}
            disabled={submitting}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            className="gym-input"
          />
        </div>

        <dl className="gym-summary gym-profile-readonly">
          <div className="gym-summary__row">
            <dt><TbLock className="gym-profile-readonly__lock" aria-hidden="true" /><span>Email</span></dt>
            <dd>{detailUser && detailUser.email}</dd>
          </div>
          <div className="gym-summary__row">
            <dt><TbLock className="gym-profile-readonly__lock" aria-hidden="true" /><span>Rol</span></dt>
            <dd>{ROLE_LABELS[detailUser && detailUser.role] || (detailUser && detailUser.role)}</dd>
          </div>
          <div className="gym-summary__row">
            <dt><TbLock className="gym-profile-readonly__lock" aria-hidden="true" /><span>Estado</span></dt>
            <dd>
              <span className={`gym-profile-readonly__status${detailUser && detailUser.isActive ? ' is-active' : ' is-inactive'}`}>
                {detailUser && detailUser.isActive ? 'Activo' : 'Inactivo'}
              </span>
            </dd>
          </div>
        </dl>

        <button type="submit" disabled={submitting} className="gym-button gym-button--primary">
          {submitting ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </form>
    </section>
  );
}
